import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getPaymentMethodsQuery } from 'query/billing';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@kizen/kds/Button';
import { AddCardModal } from '../components/AddCardModal';
import useModal from '__components/Modals/useModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import { useHistory } from 'react-router-dom';
import BillingService from 'services/BillingService';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';
import { useDispatch } from 'react-redux';
import { refreshBootstrapBusiness } from 'store/authentication/authenticationAction';
import { Header } from '../components/Table';
import DragAndDropLayout from '__components/DragAndDropLayout';
import { PaymentMethod } from '../components/PaymentMethod';
import { BillingPaymentMethods } from '@kizen/api/billing';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';

interface PaymentMethodsBlockProps {
  refetchPaymentMethods: () => void;
  paymentMethods: BillingPaymentMethods['payment_methods'];
  isSuccess: boolean;
  makePrimary: boolean;
  isLoading: boolean;
  defaultPaymentMethod?: string;
  previousExistingIds: string[];
}

export const PaymentMethodsBlock = (props: PaymentMethodsBlockProps) => {
  const {
    refetchPaymentMethods,
    paymentMethods,
    isSuccess,
    makePrimary,
    isLoading,
    defaultPaymentMethod,
    previousExistingIds,
  } = props;

  const [pendingDeleteId, setPendingDeleteId] = useState('');
  const { t } = useTranslation();
  const [showToast] = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const cardDraggerParent = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const [addCardModalProps, , addCardModal] = useModal({});

  const deletePaymentMethod = useCallback(
    async (paymentMethod: string) => {
      try {
        const res = await BillingService.deletePaymentMethod(paymentMethod);

        if (res.data.message) {
          showToast({
            message: res.data.message,
            variant:
              res.status === 200 ? toastVariant.SUCCESS : toastVariant.FAILURE,
          });
        }

        refetchPaymentMethods();
      } catch (ex: any) {
        if (typeof ex?.response?.data === 'string') {
          showToast({
            message: ex?.response?.data,
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [showToast, refetchPaymentMethods]
  );

  const [deleteCardModalProps, , deleteCardModal] = useModal({
    handleSubmit: () => deletePaymentMethod(pendingDeleteId),
  });

  const setOrderMutation = useMutation({
    mutationFn: (order: string[]) =>
      BillingService.setPaymentMethodOrder(order),
    onMutate: async (newOrder: string[]) => {
      const key = getPaymentMethodsQuery().queryKey;
      await queryClient.cancelQueries({ queryKey: key });

      const previousData = queryClient.getQueryData(key);

      const previous = structuredClone(previousData) as BillingPaymentMethods;

      const newList = previous.payment_methods.sort((a, b) => {
        return newOrder.indexOf(a.id) - newOrder.indexOf(b.id);
      });

      queryClient.setQueryData(key, {
        ...previous,
        payment_methods: newList,
      });

      return { previous: previousData, key };
    },
    onError: (err, _data, context) => {
      monitoringExceptionHelper(err);

      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSettled: (_data, _err, _variables, context) => {
      if (context) {
        queryClient.invalidateQueries({ queryKey: context.key });
      }
    },
  });

  const setOrder = useCallback(
    async (
      paymentMethodOrder: string[],
      notifyDefault = false,
      notifySuccess = true
    ) => {
      const res = await setOrderMutation.mutateAsync(paymentMethodOrder);

      if (res.status === 200) {
        if (notifyDefault) {
          showToast({
            message: t('Successfully updated default payment method'),
            variant: toastVariant.SUCCESS,
          });
        } else if (notifySuccess) {
          showToast({
            message: t('Successfully saved payment method order'),
            variant: toastVariant.SUCCESS,
          });
        }
      } else {
        showToast({
          message: t('Failed to update default payment method'),
          variant: toastVariant.FAILURE,
        });
      }
      dispatch(refreshBootstrapBusiness());
    },
    [showToast, t, dispatch, setOrderMutation]
  );

  const setDefault = useCallback(
    async (paymentMethod: string) => {
      const currentOrder = paymentMethods.map((method) => method.id);
      const newOrder = currentOrder.filter((id) => id !== paymentMethod);
      newOrder.unshift(paymentMethod);

      await setOrder(newOrder, true);
    },
    [setOrder, paymentMethods]
  );

  const getMenuHandler = useCallback(
    (paymentMethod: string) => {
      return ({ value }: { value: string }) => {
        switch (value) {
          case 'delete':
            setPendingDeleteId(paymentMethod);
            return deleteCardModal.show();
          case 'make_default':
            return setDefault(paymentMethod);
        }
      };
    },
    [deleteCardModal, setDefault]
  );

  const hasRunCheckForMethod = useRef(false);

  /*
   * Based on URL parameters, we sometimes want to set the default payment method to whichever card
   * is newly added. This is a little brittle but without handling webhooks more thoroughly it's the only
   * option. This isn't critical, since the cards will always be returned from the list, this just sets the order.
   */
  if (isSuccess) {
    showToast({
      message: t('Successfully added payment method'),
      variant: toastVariant.SUCCESS,
    });

    if (makePrimary) {
      if (!isLoading) {
        const missingMethods = paymentMethods.filter((method) => {
          return !previousExistingIds.includes(method.id);
        });

        if (missingMethods.length) {
          hasRunCheckForMethod.current = true;
          setDefault(missingMethods[0].id);
          history.replace('/settings/billing/details');
        }
      }
    } else {
      history.replace('/settings/billing/details');
    }
  }

  if (
    paymentMethods.length &&
    !defaultPaymentMethod &&
    !hasRunCheckForMethod.current
  ) {
    hasRunCheckForMethod.current = true;
    setDefault(paymentMethods[0].id);
  }

  const handleDrop = useCallback(
    (newItems: BillingPaymentMethods['payment_methods']) => {
      const order = newItems.map((item) => item.id);
      setOrder(order, false);
    },
    [setOrder]
  );

  const existingIds = useMemo(() => {
    return paymentMethods.map((method) => method.id);
  }, [paymentMethods]);

  const billingPermissions = useBillingPermissions();

  const isReadOnly =
    billingPermissions.canViewPaymentMethods &&
    !billingPermissions.canEditPaymentMethods;

  return (
    <>
      <div className="flex items-center justify-between">
        <Typography variant="header" size="xl" weight="semibold">
          {t('Payment Methods & Order')}
        </Typography>
        {isReadOnly ? null : (
          <div className="text-right">
            <Button
              onClick={addCardModal.show}
              maxWidth={false}
              variant="text"
              leftIcon="action-add"
              size="xs"
            >
              {t('Add Payment Method')}
            </Button>
          </div>
        )}
      </div>
      {paymentMethods.length ? (
        <>
          <Spacer size={20} />
          <div>
            <Header
              labels={[
                { label: t('Payment Info'), span: 3 },
                { label: t('Type') },
                { label: t('Exp Date') },
                ...(isReadOnly ? [{ label: '' }] : [{ label: t('Actions') }]),
              ]}
            />
            <div ref={cardDraggerParent}>
              <DragAndDropLayout
                items={paymentMethods}
                checkDisableDrag={() => isReadOnly}
                countItems={paymentMethods.length}
                itemElement={
                  <PaymentMethod
                    isReadOnly={isReadOnly}
                    getMenuHandler={getMenuHandler}
                  />
                }
                parentRef={cardDraggerParent}
                itemClassName="dndItemWrapper"
                handleClassName="itemTarget"
                onChange={handleDrop}
              />
            </div>
          </div>
        </>
      ) : null}
      {addCardModalProps.show ? (
        <AddCardModal {...addCardModalProps} existingIds={existingIds} />
      ) : null}
      {deleteCardModalProps.show ? (
        <ConfirmDeletionModal {...deleteCardModalProps}>
          {t('This will permanently delete this payment method.')}
        </ConfirmDeletionModal>
      ) : null}
    </>
  );
};
